import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Mail, Search, Users, MousePointerClick, Eye, MailOpen, 
  Filter, ChevronLeft, ChevronRight, Loader2, ArrowUpDown
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

export default function EmailStatsPage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [senderFilter, setSenderFilter] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('last_email_at');
  const [sortDir, setSortDir] = useState('desc');

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 25);
      if (search) params.append('search', search);
      if (statusFilter && statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (senderFilter && senderFilter !== 'all') params.append('sender_filter', senderFilter);
      const res = await api.get(`/api/admin/email-stats?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load email stats');
    } finally {
      setLoading(false);
    }
  }, [api, page, search, statusFilter, senderFilter]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => { setPage(1); }, [search, statusFilter, senderFilter]);

  const sortedRecipients = (data?.recipients || []).slice().sort((a, b) => {
    const aVal = a[sortField] || '';
    const bVal = b[sortField] || '';
    if (sortField === 'total_sent' || sortField === 'total_opens' || sortField === 'total_clicks') {
      return sortDir === 'asc' ? (a[sortField] || 0) - (b[sortField] || 0) : (b[sortField] || 0) - (a[sortField] || 0);
    }
    return sortDir === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
  });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortHeader = ({ field, label }) => (
    <th 
      className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-gray-700 select-none"
      onClick={() => toggleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${sortField === field ? 'text-[#014DB7]' : 'text-gray-300'}`} />
      </span>
    </th>
  );

  const summary = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="email-stats-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#0A1628]">Email Stats</h1>
        <p className="text-sm text-gray-500 mt-1">Track who was emailed, who opened, and who clicked across all system emails</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="rounded-xl border bg-white p-4 shadow-sm" data-testid="stat-total-recipients">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summary.total_recipients || 0}</p>
              <p className="text-xs text-gray-500">Recipients</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border bg-white p-4 shadow-sm" data-testid="stat-total-emails">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Mail className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summary.total_emails || 0}</p>
              <p className="text-xs text-gray-500">Emails Sent</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border bg-white p-4 shadow-sm" data-testid="stat-total-opens">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <MailOpen className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summary.total_opens || 0}</p>
              <p className="text-xs text-gray-500">Total Opens</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border bg-white p-4 shadow-sm" data-testid="stat-total-clicks">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <MousePointerClick className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summary.total_clicks || 0}</p>
              <p className="text-xs text-gray-500">Total Clicks</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border bg-white p-4 shadow-sm" data-testid="stat-open-rate">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Eye className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summary.open_rate || 0}%</p>
              <p className="text-xs text-gray-500">Open Rate</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border bg-white p-4 shadow-sm" data-testid="stat-click-rate">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
              <MousePointerClick className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{summary.click_rate || 0}%</p>
              <p className="text-xs text-gray-500">Click Rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[240px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input 
                placeholder="Search by name, email, or company..." 
                value={search} 
                onChange={(e) => setSearch(e.target.value)} 
                className="pl-10"
                data-testid="email-stats-search"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="email-stats-status-filter">
              <Filter className="w-4 h-4 mr-2 text-gray-400" />
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="opened">Opened</SelectItem>
              <SelectItem value="clicked">Clicked</SelectItem>
              <SelectItem value="not_opened">Not Opened</SelectItem>
            </SelectContent>
          </Select>
          <Select value={senderFilter} onValueChange={setSenderFilter}>
            <SelectTrigger className="w-[180px]" data-testid="email-stats-sender-filter">
              <SelectValue placeholder="All Senders" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Senders</SelectItem>
              {(data?.all_senders || []).map(s => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(search || statusFilter || senderFilter) && (
            <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setStatusFilter(''); setSenderFilter(''); }} className="text-gray-500">
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Recipients Table */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : sortedRecipients.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-400">
            <Mail className="w-12 h-12 mb-3" />
            <p className="text-sm">No email recipients found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <SortHeader field="name" label="Recipient" />
                    <SortHeader field="company" label="Company" />
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-left">Email</th>
                    <SortHeader field="total_sent" label="Sent" />
                    <SortHeader field="total_opens" label="Opens" />
                    <SortHeader field="total_clicks" label="Clicks" />
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-left">Sent By</th>
                    <SortHeader field="last_email_at" label="Last Email" />
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-center">Engagement</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sortedRecipients.map((r, idx) => (
                    <tr key={r.email + idx} className="hover:bg-gray-50" data-testid={`email-recipient-${idx}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{r.name || '-'}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{r.company || '-'}</td>
                      <td className="px-4 py-3 text-gray-600 font-mono text-xs">{r.email}</td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-700 font-semibold text-sm">
                          {r.total_sent}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${
                          r.total_opens > 0 ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-400'
                        }`}>
                          {r.total_opens}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-semibold text-sm ${
                          r.total_clicks > 0 ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-400'
                        }`}>
                          {r.total_clicks}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {r.senders.map((s, si) => (
                            <span key={si} className="text-xs bg-[#014DB7]/10 text-[#014DB7] px-2 py-0.5 rounded-full">{s}</span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {r.last_email_at ? new Date(r.last_email_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {r.has_opened && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold" title={r.last_opened_at ? `Last opened: ${new Date(r.last_opened_at).toLocaleString()}` : ''}>
                              <MailOpen className="w-3 h-3" /> Opened
                            </span>
                          )}
                          {r.has_clicked && (
                            <span className="inline-flex items-center gap-0.5 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold" title={r.last_clicked_at ? `Last clicked: ${new Date(r.last_clicked_at).toLocaleString()}` : ''}>
                              <MousePointerClick className="w-3 h-3" /> Clicked
                            </span>
                          )}
                          {!r.has_opened && !r.has_clicked && (
                            <span className="text-[10px] text-gray-400">No activity</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data?.total_pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <p className="text-sm text-gray-500">
                  Showing {((page - 1) * 25) + 1}–{Math.min(page * 25, data?.total || 0)} of {data?.total || 0}
                </p>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" size="sm" disabled={page <= 1} 
                    onClick={() => setPage(p => p - 1)}
                    data-testid="email-stats-prev-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-600">Page {page} of {data?.total_pages}</span>
                  <Button 
                    variant="outline" size="sm" disabled={page >= (data?.total_pages || 1)} 
                    onClick={() => setPage(p => p + 1)}
                    data-testid="email-stats-next-page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
